import {get, last} from 'lodash'
import {accountTypes} from '@bdswiss/common-enums'

type TauroFactorisationRules = {
  enabled?: boolean
  multiplier?: number
  leverageDivisor?: number
  drawdownTriggerPercent?: number
  tauroProfitSplitPercent?: number
  minQualifyingDeposit?: number
  maxQualifyingDeposit?: number
}

/**
 * The Tauro 10x factorisation config that applies to the viewer, or null when none does
 * (PT-5499/PT-5492).
 *
 * `viewer.tauroFactorisation` is the server's own answer and the one account creation is
 * gated on: it covers partners, who inherit the config of the campaign they own rather
 * than of a campaign they registered under (PT-6175). The registration campaign is only
 * a fallback for clients loaded before that field was queried.
 */
export const getViewerTauroFactorisation = (viewer: unknown): TauroFactorisationRules | null => {
  const campaign = get(last(get(viewer, 'registrationCampaigns') || []), 'campaign')
  const tauro = (get(viewer, 'tauroFactorisation') ?? get(campaign, 'tauroFactorisation')) as
    | TauroFactorisationRules
    | undefined
  return tauro && tauro.enabled === true ? tauro : null
}

/**
 * Whether the account being created will be a Tauro 10x factorised account, and so
 * must carry an explicit Terms & Conditions acceptance before it can be created.
 * Factorisation applies only to live MT5 Classic (MT5_STD) accounts of clients under
 * an enabled 10x campaign; `accountSubtype` is the wizard's raw selection and may be
 * empty while the account type resolves it implicitly.
 */
export const requiresTauroTermsAcceptance = (
  viewer: unknown,
  accountType?: string,
  accountSubtype?: string,
): boolean => {
  if (!accountType || !accountTypes[accountType]) return false
  const type = accountTypes[accountType]
  if (type.category !== 'forexMt5' || type.isDemo) return false
  if (accountSubtype && accountSubtype !== 'mt5Std') return false
  return getViewerTauroFactorisation(viewer) != null
}
